// src/frontend/js/modern/modules/wishlist/index.js

import { eventManager } from '../../core/event-manager.js';
import { eventBus, EVENTS } from '../../core/event-bus.js';
import { fragmentManager } from '../../core/fragment-manager.js';
import wishlistModuleStore from './store.js';
import wishlistActions from './actions.js';
import wishlistUI from './ui.js';
import { handleVariationShown, handleVariationReset } from '../../shared/variation-handler.js';
import { showError, showSuccess } from '../../shared/ui-helpers.js';
import { queueManager } from '../../core/queue.js';
import { showPopup, getOrCreate } from '../../lib/popup-lite.js';
import validators from '../../shared/validators.js';
import {
	buildFormData,
	extractParentProductId,
	extractProductId,
	getItemIdFromElement,
	getWishlistIdFromElement
} from "../../shared/dom-helpers";
import {
	shouldSendAddToCartRequest,
	triggerAddingToCart,
	triggerAddedToCartEvents,
	triggerAjaxRequestNotSent
} from '../../shared/cart-helpers.js';
import { applyModuleFilter } from '../../shared/filter-helpers.js';
import { ensurejQuery } from "../../shared/ui-helpers";
import stateCache from "../../core/state-cache";
import multiListActions from '../multi-list/actions.js';

const wishlistHandlers = [
	{
		selector: '.wlfmc_add_to_wishlist',
		eventName: 'click',
		handler: (event, element) => {
			const productId = extractProductId(element);
			if (!productId) return;

			// Merge-list mode delegates to multi-list module.
			if (window.wlfmc_l10n?.merge_lists === 'true') return;

			const parentProductId = extractParentProductId(element) || productId;
			const { formData, quantity, productType } = buildFormData(element, productId, parentProductId);

			if (!formData.has('add_to_wishlist')) formData.append('add_to_wishlist', String(productId));
			if (!formData.has('product_type')) formData.append('product_type', productType);

			// Build filter data for third-party plugins
			const filterData = {
				add_to_wishlist: productId,
				product_type: productType,
				quantity,
			};

			// Third-party plugins may mutate filterData in place OR return a new object.
			// applyModuleFilter handles both patterns and always returns the final data.
			const filteredResult = applyModuleFilter('wlfmc_add_to_wishlist_data', element, filterData);

			// Sync all keys from the filter result into formData.
			// We do NOT check reference inequality because third-party code
			// typically mutates the object in place (same reference, new keys).
			if (filteredResult && typeof filteredResult === 'object') {
				Object.entries(filteredResult).forEach(([key, value]) => {
					if (!formData.has(key)) {
						formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
					}
				});
			}

			if (!validators.isCookieEnabled()) {
				window.alert(window.wlfmc_l10n?.labels?.cookie_disabled);
				return;
			}

			const jQuery = window.jQuery;
			if (jQuery) jQuery(document.body).trigger('wlfmc_adding_to_wishlist');

			// Redirect mode: submit a real form (matches legacy behavior).
			if (window.wlfmc_l10n?.click_behavior === 'add-redirect') {
				const form = document.createElement('form');
				form.method = 'POST';
				form.action = window.location.origin + window.location.pathname +
					'?add_to_wishlist=' + encodeURIComponent(productId);
				form.style.display = 'none';

				let hasFiles = false;
				formData.forEach((value, key) => {
					if (key === 'add_to_wishlist') return;
					if (value instanceof File) {
						hasFiles = true;
						const dt = new DataTransfer();
						dt.items.add(value);
						const input = document.createElement('input');
						input.type = 'file';
						input.name = key;
						input.files = dt.files;
						form.appendChild(input);
					} else {
						const input = document.createElement('input');
						input.type = 'hidden';
						input.name = key;
						input.value = value;
						form.appendChild(input);
					}
				});
				if (hasFiles) form.enctype = 'multipart/form-data';
				document.body.appendChild(form);
				form.submit();
				return;
			}

			wishlistActions.add(productId, formData, { element, data: { quantity } });
		},
		priority: 100,
	},
	{
		selector: '.wlfmc-wishlist-table .remove_from_wishlist',
		eventName: 'click',
		handler: (event, element) => {
			const productId = extractProductId(element);
			if (!productId) return;

			const wishlistId = getWishlistIdFromElement(element);
			const nonce = element.dataset.nonce || element.getAttribute('data-nonce');
			const table = element.closest('.wlfmc-wishlist-items-wrapper');
			const wishlistToken = table?.dataset?.token || table?.getAttribute('data-token') || '';
			const row = element.closest('[data-row-id]');

			if (row) {
				row.classList.add('disabled-row');
			}
			const isMerge = stateCache.getConfig()?.mergeLists === true;
			const wishlistTable = element.closest('.wlfmc-wishlist-table');
			const isMultiListTable = String(wishlistTable?.dataset.wishlistType || '') === 'lists';
			const listId = parseInt(wishlistId, 10) || 0;
			// Removing from an individual multi-list table must only affect
			// that specific list. It must not remove the product from raw
			// wishlist or from other lists simultaneously.
			if (listId > 0 && (isMerge || isMultiListTable)) {
				multiListActions.removeFromList(productId, wishlistId, nonce, element, wishlistToken);
				return;
			}

			wishlistActions.remove(productId, wishlistId, wishlistToken, nonce, element);
		},
		priority: 90,
	},
	{
		selector: '.wlfmc_delete_item',
		eventName: 'click',
		handler: (event, element) => {
			const itemId = getItemIdFromElement(element);
			const productId = extractProductId(element) || 0;
			const wishlistId = getWishlistIdFromElement(element);
			const nonce = element.dataset.nonce || element.getAttribute('data-nonce');

			if (!itemId && !productId) return;

			wishlistActions.deleteByItemId(productId || 0, itemId, wishlistId, nonce, element);
		},
		priority: 100,
	},
	{
		selector: '.wlfmc_ajax_add_to_cart:not(.disabled)',
		eventName: 'click',
		handler: (event, element) => {
			const itemId = getItemIdFromElement(element);
			const wishlistId = getWishlistIdFromElement(element);
			const nonce = element.dataset.nonce || element.getAttribute('data-nonce');

			if (!itemId) return;

			if (!shouldSendAddToCartRequest(element)) {
				triggerAjaxRequestNotSent(element);
				return;
			}

			element.classList.remove('added');
			element.classList.add('loading');

			const addToCartData = { action: 'wlfmc_add_to_cart', lid: itemId, wid: wishlistId, nonce };
			triggerAddingToCart(element, addToCartData);

			queueManager.addWithDeduplication('add_to_cart', addToCartData)
				.then((result) => {
					if (result.error) {
						if (result.product_url) { window.location = result.product_url; return; }
						showError(result.error || window.wlfmc_l10n?.labels?.failed_add_to_cart_message);
						return;
					}

					if (typeof wc_add_to_cart_params !== 'undefined' && validators.isTrue(wc_add_to_cart_params.cart_redirect_after_add)) {
						window.location = wc_add_to_cart_params.cart_url;
						return;
					}

					triggerAddedToCartEvents(result.fragments, result.cart_hash, element);

					if (result.fragments) {
						const miniCart = document.querySelector('div.widget_shopping_cart_content');
						if (miniCart && result.fragments['div.widget_shopping_cart_content']) {
							miniCart.outerHTML = result.fragments['div.widget_shopping_cart_content'];
						}
					}

					if (window.wlfmc_l10n?.labels?.added_to_cart_message) {
						showSuccess(window.wlfmc_l10n.labels.added_to_cart_message);
					}

					if (window.wlfmc_l10n?.remove_from_wishlist_after_add_to_cart) {
						const removeBtn = element.closest('tr')?.querySelector('.remove_from_wishlist');
						if (removeBtn) removeBtn.dispatchEvent(new Event('click', { bubbles: true, cancelable: true }));
					}

					if (result.message) {
						const jQuery = window.jQuery;
						if (jQuery) {
							jQuery(document.body).trigger('add_to_cart_message', [result.message, ensurejQuery(element)]);
						} else {
							document.body.dispatchEvent(new CustomEvent('add_to_cart_message', {
								bubbles: true,
								detail: { message: result.message, button: element }
							}));
						}
					}
				})
				.catch((error) => {
					showError(error.message || window.wlfmc_l10n?.labels?.add_to_cart_failed);
				})
				.finally(() => element.classList.remove('loading'));
		},
		priority: 80,
	},
	{
		selector: '.wlfmc_already_in_wishlist',
		eventName: 'click',
		handler: (event) => {
			event.preventDefault();
			showError(window.wlfmc_l10n?.labels?.already_in_wishlist_text);
		},
		priority: 50,
	},
	{
		selector: '.wlfmc-btn-login-need',
		eventName: 'click',
		handler: (event) => {
			event.preventDefault();
			showError(window.wlfmc_l10n?.labels?.login_need);
		},
		priority: 50,
	},
	{
		selector: '.wlfmc-popup-trigger:not(.wlfmc-disabled):not(.wlfmc-waitlist-btn-login-need):not(.wlfmc-multi-list-btn-login-need)',
		eventName: 'click',
		handler: (event, element) => {
			event.preventDefault();
			const popupId = element.dataset.popupId;
			if (!popupId) return;

			const popup = document.getElementById(popupId);
			if (!popup) return;

			const tooltip = document.getElementById('wlfmc-tooltip');
			if (tooltip) {
				tooltip.style.top = '0';
				tooltip.style.left = '0';
				tooltip.className = 'tooltip__hidden';
			}

			getOrCreate(popup, {
				absolute: false,
				color: '#333',
				transition: 'all 0.3s',
				horizontal: popup.dataset.horizontal || 'center',
				vertical: popup.dataset.vertical || 'center'
			});

			showPopup(popup);
		},
		priority: 70,
	},
];

const wishlistModule = {
	name: 'wishlist',
	store: wishlistModuleStore,
	actions: wishlistActions,
	ui: wishlistUI,

	init() {
		eventManager.registerModuleHandlers('wishlist', wishlistHandlers);

		eventBus.on('variation:shown', (data) => handleVariationShown(data, 'wishlist', wishlistModuleStore, wishlistUI));
		eventBus.on('variation:reset', (data) => handleVariationReset(data.productId, 'wishlist', wishlistModuleStore, wishlistUI));

		eventBus.on('cache:apply', () => this.applyState());
		eventBus.on('store:sync-wishlist', (data) => {
			wishlistModuleStore.syncFromData(data);
			this.applyState();
		});
		eventBus.on('system:data-ready', () => this.applyState());

		eventBus.on(EVENTS.SYNC_COMPLETED, (result) => {
			if (result?.remoteTriggered) {
				fragmentManager.loadFragments({ s: 'wishlist' });
			}
			this.applyState();
		});

		eventBus.on('counter:remove', ({ isWishlist, productId, wishlistId, nonce, wishlistToken, element = null }) => {
			if (!isWishlist) return;
			// In merge mode, delegate to multi-list module for proper removal
			const isMerge = stateCache.getConfig()?.mergeLists === true;
			if (isMerge && wishlistId) {
				// FIX: Immediately repoint the DOM row to the next list that
				// still contains this product. Without this, rapid clicks
				// within the optimistic-ui delay window re-read the stale
				// data-wishlist-id and keep sending the previous list's ID.
				if (element) {
					const row = element.closest('[data-row-id]');
					if (row) {
						const lists = stateCache.getLists();
						const nextList = lists.find(l => {
							if (String(l.wishlist_id ?? l.id) === String(wishlistId)) return false;
							return (l.wishlist_items || []).some(
								i => String(i.product_id) === String(productId)
							);
						});
						if (nextList) {
							const nextListId = String(nextList.wishlist_id ?? nextList.id ?? '');
							row.dataset.wishlistId = nextListId;
							row.setAttribute('data-wishlist-id', nextListId);
							const nextItem = (nextList.wishlist_items || []).find(
								i => String(i.product_id) === String(productId)
							);
							if (nextItem) {
								if (nextItem.remove_nonce) {
									const removeLink = row.querySelector('.remove_from_wishlist');
									if (removeLink) {
										removeLink.setAttribute('data-nonce', nextItem.remove_nonce);
									}
								}
								if (nextItem.wishlist_token) {
									row.setAttribute('data-wishlist-token', nextItem.wishlist_token);
									row.dataset.wishlistToken = nextItem.wishlist_token;
								}
							}
						}
					}
				}
				// Use multi-list remove which handles merge mode correctly
				multiListActions.removeFromList(productId, wishlistId, nonce, element);
				return;
			}
			// Non-merge mode: use standard wishlist remove
			const item = wishlistModuleStore.getItem(productId);
			wishlistActions.counterRemove(
				productId,
				item?.wishlist_id || wishlistId || 0,
				item?.remove_nonce || nonce || '',
				item?.wishlist_token || wishlistToken || '',
				element
			);
		});
		eventBus.on('cart:item_added_to_cart', ({ rowId, wishlistId, listType, isUserOwner }) => {
			if (listType !== 'wishlist' || !isUserOwner) return;
			wishlistActions.remove(rowId, wishlistId);
		});
	},

	applyState() {
		// All counter, mini-list, and add-button rendering is handled
		// reactively by WishlistUI.renderFromState() via the stateCache
		// subscription. applyState is kept as a no-op hook for future
		// imperative needs (e.g. fragment loading on remote sync).
	},

	getStatus() {
		return { count: wishlistModuleStore.count.get(), items: wishlistModuleStore.items.get().length };
	},
};

export default wishlistModule;
export { wishlistModuleStore, wishlistActions, wishlistUI };
